import React, { useState, useMemo } from 'react'
import { Tab } from '@mui/material'
import {
  Wrapper,
  TabsContainer,
  StyledTabs,
  ControlsBar,
  SearchAndFilter,
  SearchInput,
  TasksTable,
  TableContainer,
  HeaderRow,
  SortableHeaderCell,
  FilterText,
} from './styles'
import { Button } from '@mui/material'
import useModals from '@/v2/hooks/useModals'
import { useTasks } from '@/v2/hooks/useTasks'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import RTTasks from '@/v2/hooks/subscriptions/messages/RTTasks'
import TaskRowComponent from './components/TaskRow'

const Tasks = () => {
  const [tabValue, setTabValue] = useState(0)
  const { goTo } = useModals()
  const { tasks = [], isLoading } = useTasks()
  const [sortDirection, setSortDirection] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all') // 'all', 'completed', 'pending'

  const handleSortByDueDate = () => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    setSortDirection(newDirection)
  }

  const handleAddTask = () => {
    goTo('/v2/task/new')
  }

  const filteredAndSortedTasks = useMemo(() => {
    // First filter by status
    const statusFiltered = tasks.filter(task => {
      if (statusFilter === 'all') return true
      if (statusFilter === 'completed') return task.completed
      if (statusFilter === 'pending') return !task.completed
      return true
    })

    // Then filter by search
    const searchFiltered = searchQuery
      ? statusFiltered.filter(task => {
          const searchLower = searchQuery.toLowerCase()
          return (
            task.title?.toLowerCase().includes(searchLower) ||
            task.patient?.fullName.toLowerCase().includes(searchLower) ||
            task.assignedBy?.fullName?.toLowerCase().includes(searchLower)
          )
        })
      : statusFiltered

    // Then sort
    return searchFiltered.sort((a, b) => {
      // First sort by completion status (incomplete first)
      if (a.completed !== b.completed) {
        return a.completed ? 1 : -1
      }
      // Then sort by creation date (newer first)
      const dateA = new Date(a.createdAt)
      const dateB = new Date(b.createdAt)
      return dateB - dateA
    })
  }, [tasks, searchQuery, statusFilter])

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Wrapper>
      <TabsContainer>
        <StyledTabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
        >
          <Tab label={`Tasks (${tasks.length})`} />
        </StyledTabs>
      </TabsContainer>

      <ControlsBar>
        <SearchAndFilter>
          <SearchInput
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <FilterText
            onClick={() =>
              setStatusFilter(status => {
                if (status === 'all') return 'completed'
                if (status === 'completed') return 'pending'
                return 'all'
              })
            }
          >
            Filter by:{' '}
            {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
          </FilterText>
        </SearchAndFilter>
        <Button variant="contained" onClick={handleAddTask}>
          Add Task
        </Button>
      </ControlsBar>

      <TasksTable>
        <HeaderRow>
          <div></div>
          <div>Task Name</div>
          <div>Created</div>
          <SortableHeaderCell
            onClick={handleSortByDueDate}
            $sortDirection={sortDirection}
          >
            Due Date
          </SortableHeaderCell>
          <div>Patient</div>
          <div>Assigned To</div>
          <div></div>
        </HeaderRow>
        <TableContainer>
          {filteredAndSortedTasks.map(task => (
            <TaskRowComponent key={task.id} task={task} />
          ))}
        </TableContainer>
      </TasksTable>
      <RTTasks />
    </Wrapper>
  )
}

export default Tasks
